'use client';

import { useEffect } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import {
  doAPI,
  getPreparedKey,
  type TApiArgs,
  type TApiKeysByNamespace,
  type TNamespaces,
  type TUseApiReturn,
} from '@/services/utils';
import Toast from '@/components/Toast';
import { useAuthCSRSession } from '@/modules/auth/hooks/AuthSession';

export const useAPI = <
  Namespace extends TNamespaces,
  Key extends TApiKeysByNamespace<Namespace>,
  IsMutation extends boolean,
  IsInfinite extends boolean,
>(
  keyByNamespace: [Namespace, Key, ...any[]],
  args: Omit<
    TApiArgs<Namespace, Key, IsMutation, IsInfinite>,
    'session' | 'context'
  >
) => {
  const { getSession } = useAuthCSRSession();
  const useRequest: TUseApiReturn<Namespace, Key, IsMutation, IsInfinite> = (
    args?.isMutation
      ? useMutation
      : args?.isInfinite
        ? useInfiniteQuery
        : useQuery
  ) as TUseApiReturn<Namespace, Key, IsMutation, IsInfinite>;

  const result = useRequest(
    getPreparedKey(keyByNamespace, args),
    async (variables: any = undefined) =>
      doAPI<Namespace, Key>([keyByNamespace[0], keyByNamespace[1]], {
        ...args,
        input: {
          ...(args?.input || {}),
          ...(variables?.pageParam || variables || {}),
          queryKey: undefined,
          signal: undefined,
        },
        axiosRequestConfig: {
          signal: variables?.signal,
          ...args?.axiosRequestConfig,
        },
        session: getSession(),
      } as any),
    args?.reactQueryOptions
  );

  const { error } = result;

  useEffect(() => {
    if (error && args?.toastOnError) {
      Toast.open({ variant: 'error', children: `${error.message || error}` });
    }
  }, [error, args?.toastOnError]);

  return result;
};
